import { getAccessToken } from '../../../api/clients/tokenAccessor';
import type { Attachment } from '../types';

export type AttachmentKind = 'image' | 'pdf' | 'audio' | 'document';

export const MAX_FILES_PER_SUBMIT = 5;

export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export const PDF_MIME_TYPES = ['application/pdf'] as const;

export const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
] as const;

export const DOCUMENT_MIME_TYPES = [
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/rtf', // .rtf
  'text/rtf', // .rtf (alternate)
  'text/plain', // .txt
] as const;

// Some browsers report an empty or generic MIME type for .doc/.txt/.rtf
// files, so we also let the file picker match on extension.
export const DOCUMENT_EXTENSIONS = ['.doc', '.docx', '.rtf', '.txt'] as const;

export const ATTACHMENT_ACCEPT = [
  ...IMAGE_MIME_TYPES,
  ...PDF_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES,
  ...DOCUMENT_EXTENSIONS,
].join(',');

const SIZE_CAPS_MB: Record<AttachmentKind, number> = {
  image: 25,
  pdf: 50,
  audio: 50,
  document: 25,
};

export const ALLOWED_MIME_BY_KIND: Record<AttachmentKind, readonly string[]> = {
  image: IMAGE_MIME_TYPES,
  pdf: PDF_MIME_TYPES,
  audio: AUDIO_MIME_TYPES,
  document: DOCUMENT_MIME_TYPES,
};

export function classifyKind(mime: string, fileName?: string): AttachmentKind | null {
  if (IMAGE_MIME_TYPES.includes(mime as (typeof IMAGE_MIME_TYPES)[number])) return 'image';
  if (PDF_MIME_TYPES.includes(mime as (typeof PDF_MIME_TYPES)[number])) return 'pdf';
  if (AUDIO_MIME_TYPES.includes(mime as (typeof AUDIO_MIME_TYPES)[number])) return 'audio';
  if (DOCUMENT_MIME_TYPES.includes(mime as (typeof DOCUMENT_MIME_TYPES)[number])) return 'document';
  // Fall back to the file extension when the browser supplies an empty or
  // unrecognized MIME type (common for .doc/.txt/.rtf).
  if (fileName) {
    const lower = fileName.toLowerCase();
    if (DOCUMENT_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'document';
  }
  return null;
}

export type FileValidationResult =
  | { ok: true; kind: AttachmentKind }
  | { ok: false; error: string };

export function validateFile(file: File): FileValidationResult {
  const kind = classifyKind(file.type, file.name);
  if (!kind) {
    return {
      ok: false,
      error: `"${file.name}" is not a supported type (images, PDFs, audio, or documents).`,
    };
  }
  const capMb = SIZE_CAPS_MB[kind];
  const capBytes = capMb * 1024 * 1024;
  if (file.size > capBytes) {
    return {
      ok: false,
      error: `"${file.name}" exceeds the ${capMb}MB cap for ${kind} files.`,
    };
  }
  return { ok: true, kind };
}

// Maps a document file extension to a canonical MIME type. Used as a fallback
// when the browser supplies an empty/generic Content-Type, since the presigned
// S3 PUT requires a concrete Content-Type to match the signature.
const EXTENSION_CONTENT_TYPE: Record<string, string> = {
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.rtf': 'application/rtf',
  '.txt': 'text/plain',
};

export function resolveContentType(file: File): string {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  for (const [ext, mime] of Object.entries(EXTENSION_CONTENT_TYPE)) {
    if (lower.endsWith(ext)) return mime;
  }
  return file.type;
}

export interface PresignedUpload {
  fileName: string;
  s3Key: string;
  presignedUrl: string;
  kind: AttachmentKind;
}

export async function presignUploads(
  apiBase: string,
  threadId: string,
  files: File[],
): Promise<PresignedUpload[]> {
  let authHeaders: Record<string, string> = {};
  try {
    const token = await getAccessToken();
    authHeaders = { Authorization: `Bearer ${token}` };
  } catch {
    // Token not available — proceed without auth (server will reject if needed)
  }

  const res = await fetch(`${apiBase}/upload-media`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      threadId,
      files: files.map((f) => {
        const kind = classifyKind(f.type, f.name);
        if (!kind) throw new Error(`Unsupported file type: ${f.type || f.name}`);
        return {
          fileName: f.name,
          fileSize: f.size,
          contentType: resolveContentType(f),
          kind,
        };
      }),
    }),
  });

  if (!res.ok) {
    let errorText = `upload-media ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) errorText = body.error;
    } catch {
      // ignore parse errors
    }
    throw new Error(errorText);
  }

  const body = await res.json();
  if (!Array.isArray(body?.uploads)) {
    throw new Error('upload-media: malformed response (uploads is not an array)');
  }
  if (body.uploads.length !== files.length) {
    throw new Error(
      `upload-media: expected ${files.length} presigned upload(s), got ${body.uploads.length}`,
    );
  }
  const uploads = body.uploads as unknown[];
  uploads.forEach((u, idx) => {
    const upload = u as Partial<PresignedUpload> | null;
    if (
      !upload ||
      typeof upload.presignedUrl !== 'string' ||
      !upload.presignedUrl ||
      typeof upload.s3Key !== 'string' ||
      !upload.s3Key
    ) {
      throw new Error(`upload-media: malformed upload entry at index ${idx}`);
    }
  });
  return uploads as PresignedUpload[];
}

export async function uploadToS3(
  file: File,
  presignedUrl: string,
  contentType: string,
  signal?: AbortSignal,
): Promise<void> {
  if (!contentType) {
    throw new Error(`Missing Content-Type for ${file.name}; presigned PUT will fail.`);
  }
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
    signal,
  });
  if (!res.ok) {
    throw new Error(`S3 PUT ${res.status} for ${file.name}`);
  }
}

/**
 * Presign + parallel-PUT a batch of files for a thread, returning the
 * resulting Attachment metadata ready to be sent on the /invoke message.
 * `previewUrls[i]` (if supplied) is carried through onto the attachment so
 * the UI can keep showing the local image preview in-session.
 */
export async function uploadAttachments(
  apiBase: string,
  threadId: string,
  files: File[],
  previewUrls: Array<string | undefined> = [],
): Promise<Attachment[]> {
  // Capture the contentType per file up front and reuse the same value for
  // the presign declaration and the S3 PUT — the presigned URL is signed
  // against this exact Content-Type, so any drift causes SignatureDoesNotMatch.
  const contentTypes = files.map((f) => resolveContentType(f));
  const presigned = await presignUploads(apiBase, threadId, files);
  await Promise.all(
    files.map((file, idx) =>
      uploadToS3(file, presigned[idx].presignedUrl, contentTypes[idx]),
    ),
  );
  return files.map((file, idx) => ({
    s3Key: presigned[idx].s3Key,
    contentType: contentTypes[idx],
    kind: presigned[idx].kind,
    fileName: file.name,
    byteSize: file.size,
    previewUrl: previewUrls[idx],
  }));
}
